import { useCallback } from "react";
import {
  actions,
  useMountedLayoutEffect,
  useGetLatest,
  makePropGetter,
} from "react-table";
import cn from "classnames";
import { useOnClickOutside } from "./useOnClickOutside";

const pluginName = "useMenu";

actions.toggleMenuVisibility = "toggleMenuVisibility";
actions.activeMenuRow = "activeMenuRow";
actions.resetMenu = "resetMenu";

export const useMenu = (hooks) => {
  hooks.getMenuRowCellProps = [defaultGetToggleMenuRowCellsProps];
  hooks.getMenuStyleProps = [defaultStyleMenuProps];

  hooks.stateReducers.push(reducer);
  hooks.useInstance.push(useInstance);
  hooks.prepareRow.push(prepareRow);
};

useMenu.pluginName = pluginName;

const defaultGetToggleMenuRowCellsProps = (
  props,
  { instance, row, userProps }
) => {
  const {
    state: { activeMenu, isActiveMenu },
    dispatch,
  } = instance;

  return [
    props,
    {
      onClick: () => {
        dispatch({
          type: actions.toggleMenuVisibility,
          payload: {
            activeMenu: activeMenu === row?.id ? null : row?.id,
            isActiveMenu: !isActiveMenu,
          },
        });
      },
    },
  ];
};

const defaultStyleMenuProps = (props, { instance, row, userProps }) => {
  const {
    state: { activeMenu, isActiveMenu },
    dispatch,
    page
  } = instance;
  const { menuRef, triggerRefs, tableRef } = userProps;

  /**
   * The function calculates the position of a menu based on the available space on the table and the position of the trigger element.
   * @returns The function `calculateMenuPosition` returns a string value. The possible return values are tailwind css; "bottom-5" or "top-5".
   *
   */
  const calculateMenuPosition = (index) => {
    if (activeMenu === index && triggerRefs.current[index]) {
      let tableHeight = tableRef?.current?.getBoundingClientRect?.()?.height;
      let triggerHeight =
        triggerRefs?.current?.[index]?.getBoundingClientRect?.().height;

      const availableSpace = tableHeight - (triggerHeight * index);

      if (availableSpace < 500 && page?.length > 5) {
        // Adjust the menu height as per your requirements
        return "bottom-5";
      } else {
        return "top-5";
      }

    }
  };

  return [
    props,
    {
      className: cn(
        "absolute w-52 lg:right-4 right-4 h-fit bg-white rounded-lg z-10 shadow",
        { hidden: row?.id !== activeMenu },
        calculateMenuPosition(row?.id)
      ),
    },
  ];
};

const reducer = (state, action, previousState, instance) => {
  if (action?.type === actions?.init) {
    return {
      activeMenu: null,
      isActiveMenu: false,
      ...state,
    };
  }

  const {
    state: { activeMenu, isActiveMenu },
    dispatch,
  } = instance;

  if (action?.type === actions?.toggleMenuVisibility) {
    const { activeMenu, isActiveMenu } = action?.payload;

    return {
      ...state,
      activeMenu,
    };
  }
};

const useInstance = (instance) => {
  const {
    rows,
    autoResetEditing = true,
    plugins,
    state: { activeMenu, isActiveMenu },
    dispatch,
    data,
    enableEditing = false,
    rowsById,
  } = instance;

  // ensurePluginOrder(
  //   plugins,
  //   [
  //     "useGlobalFilter",
  //     "useFilters",
  //     "useGroupBy",
  //     "useSortBy",
  //     "useExpanded",
  //     "usePagination",
  //   ],
  //   "useEditTableCell",
  //   "useMenu"
  // );

  const onHideMenu = () =>
    dispatch({
      type: actions.toggleMenuVisibility,
      payload: {
        activeMenu: null,
        isActiveMenu: false,
      },
    });

  const getAutoResetEditing = useGetLatest(autoResetEditing);

  useMountedLayoutEffect(() => {
    if (getAutoResetEditing()) {
      dispatch({ type: actions.resetMenu });
    }
  }, [dispatch]);

  // console.log(instance);
  Object.assign(instance, { onHideMenu });
};

const prepareRow = (row, { instance }) => {
  row.getMenuRowCellProps = makePropGetter(
    instance.getHooks().getMenuRowCellProps,
    { instance: instance, row }
  );
  row.getMenuStyleProps = makePropGetter(
    instance.getHooks().getMenuStyleProps,
    { instance: instance, row }
  );
};
